import { useCallback, useMemo, useReducer, useState } from 'react'

import type { ContactSelection } from '@/app/types/contact'

export const SELECT_HEADER_ICONS = {
  ALL: 'check_box',
  SOME: 'indeterminate_check_box',
  NONE: 'check_box_outline_blank',
} as const

type SelectionState = {
  groups: number[]
  isAll: boolean
  list: number[]
  search: string
}

type SelectionAction =
  | { type: 'add'; items: number[] }
  | { type: 'add-group'; groups: number[] }
  | { type: 'add-search'; search: string }
  | { type: 'clear' }
  | { type: 'remove'; items: number[]; selectionTotal: number }
  | { type: 'set-all'; isAll: boolean }

type Props = {
  ordered: number[]
  orgTotal: number
  total: number
}

type Result = {
  isSelectAllNotificationOpen: boolean
  selection: ContactSelection
  closeSelectAllNotification: () => void
}

const initialState: SelectionState = {
  groups: [],
  isAll: false,
  list: [],
  search: '',
}

const isExclusionMode = ({ groups, isAll, search }: SelectionState) => isAll || groups.length > 0 || search !== ''

const addItems = (itemsToAdd: number[] = [], list: number[] = []) => [...new Set([...list, ...itemsToAdd])]

const removeItems = (itemsToRemove: number[] = [], list: number[] = []) => {
  const items = new Set(itemsToRemove)
  return list.filter((itemId) => !items.has(itemId))
}

const selectionReducer = (state: SelectionState, action: SelectionAction): SelectionState => {
  switch (action.type) {
    case 'add':
      return {
        ...state,
        list: isExclusionMode(state) ? removeItems(action.items, state.list) : addItems(action.items, state.list),
      }
    case 'add-group':
      return {
        ...state,
        groups: action.groups,
        isAll: false,
        list: [],
      }
    case 'add-search':
      return {
        ...state,
        isAll: false,
        list: [],
        search: action.search,
      }
    case 'clear':
      return initialState
    case 'remove': {
      if (!isExclusionMode(state)) {
        return {
          ...state,
          list: removeItems(action.items, state.list),
        }
      }

      const list = addItems(action.items, state.list)
      if (list.length >= action.selectionTotal) {
        return initialState
      }

      return {
        ...state,
        list,
      }
    }
    case 'set-all':
      return action.isAll
        ? {
            groups: [],
            isAll: true,
            list: [],
            search: '',
          }
        : initialState
    default:
      return state
  }
}

const getHeaderIconNotAll = (list: number[], currentView: number[]) => {
  const countView = currentView.reduce((count, itemId) => count + (list.includes(itemId) ? 1 : 0), 0)

  if (countView === 0) {
    return SELECT_HEADER_ICONS.NONE
  }
  if (countView === currentView.length) {
    return SELECT_HEADER_ICONS.ALL
  }
  return SELECT_HEADER_ICONS.SOME
}

const getHeaderIconAll = (list: number[], total: number) => {
  if (total === 0 || list.length >= total) {
    return SELECT_HEADER_ICONS.NONE
  }
  if (list.length === 0) {
    return SELECT_HEADER_ICONS.ALL
  }
  return SELECT_HEADER_ICONS.SOME
}

export const getHeaderIcon = (
  isAll: boolean,
  list: number[],
  currentView: number[],
  total: number,
  groups: number[] = [],
  search = '',
) => {
  if (isAll || groups.length > 0 || search !== '') {
    return getHeaderIconAll(list, total)
  }
  return getHeaderIconNotAll(list, currentView)
}

const useSelectableTable = ({ ordered, orgTotal, total }: Props): Result => {
  const [state, dispatch] = useReducer(selectionReducer, initialState)
  const [isSelectAllNotificationOpen, setIsSelectAllNotificationOpen] = useState(false)
  const selectionTotal = state.isAll ? orgTotal : total
  const count = isExclusionMode(state) ? Math.max(0, selectionTotal - state.list.length) : state.list.length
  const headerIcon = getHeaderIcon(state.isAll, state.list, ordered, selectionTotal, state.groups, state.search)

  const add = useCallback((items: number[]) => {
    dispatch({ type: 'add', items })
  }, [])

  const addGroup = useCallback((groups: number[]) => {
    setIsSelectAllNotificationOpen(false)
    dispatch({ type: 'add-group', groups })
  }, [])

  const addSearch = useCallback((search: string) => {
    setIsSelectAllNotificationOpen(false)
    dispatch({ type: 'add-search', search })
  }, [])

  const clearSelection = useCallback(() => {
    setIsSelectAllNotificationOpen(false)
    dispatch({ type: 'clear' })
  }, [])

  const isSelected = useCallback(
    (itemId: number) => (isExclusionMode(state) ? !state.list.includes(itemId) : state.list.includes(itemId)),
    [state],
  )

  const remove = useCallback(
    (items: number[]) => {
      dispatch({ type: 'remove', items, selectionTotal })
    },
    [selectionTotal],
  )

  const setAll = useCallback((isAll: boolean) => {
    setIsSelectAllNotificationOpen(isAll)
    dispatch({ type: 'set-all', isAll })
  }, [])

  const closeSelectAllNotification = useCallback(() => {
    setIsSelectAllNotificationOpen(false)
  }, [])

  const selection = useMemo<ContactSelection>(
    () => ({
      count,
      groups: state.groups,
      headerIcon,
      isAll: state.isAll,
      list: state.list,
      search: state.search,
      add,
      addGroup,
      addSearch,
      clearSelection,
      isSelected,
      remove,
      setAll,
    }),
    [
      count,
      headerIcon,
      state.groups,
      state.isAll,
      state.list,
      state.search,
      add,
      addGroup,
      addSearch,
      clearSelection,
      isSelected,
      remove,
      setAll,
    ],
  )

  return {
    isSelectAllNotificationOpen,
    selection,
    closeSelectAllNotification,
  }
}

export default useSelectableTable
